import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LeaseDTO,
  LeaseStatus,
  LeaseTenantAddInput,
  LeaseTermType,
  LeaseTenantRole,
  LeaseUpdateInput,
  LedgerEntryTypeInput,
  LedgerMethodInput,
  PostEntryInput,
  ScheduleReplaceInput,
} from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { describeBalance, formatCents } from '../../lib/money';
import { formatDate } from '../../lib/date';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';
import { Dialog } from '../../components/a11y/Dialog';
import { LeasePaymentActivity } from '../../components/payments/PaymentActivity';
import { useAuth } from '../../providers/AuthProvider';

/**
 * Lease detail — the admin single-lease workbench (mirrors the {@link AdminTenants} pattern):
 * one react-query read of the full {@link LeaseDTO} plus the per-lease ledger, with sectioned
 * editors (core / tenants / schedule / ledger / executed-PDF) each in an accessible {@link Dialog}.
 * All writes go through the shared `api` client (CSRF handled there) and invalidate the lease
 * and/or ledger query on success.
 */

const LEASE_STATUSES: LeaseStatus[] = ['draft', 'pending_move_in', 'active', 'ended', 'terminated'];
const TERM_TYPES: LeaseTermType[] = ['fixed', 'month_to_month'];
const TENANT_ROLES: LeaseTenantRole[] = ['primary', 'co_tenant', 'guarantor', 'occupant'];
const ENTRY_TYPES: LedgerEntryTypeInput[] = ['charge', 'payment', 'credit', 'fee', 'refund'];
const LEDGER_METHODS: LedgerMethodInput[] = ['check', 'money_order', 'cashier_check', 'cash'];

/** Empty → undefined, so optional string fields are omitted rather than sent as "". */
function opt(v: string): string | undefined {
  const t = v.trim();
  return t.length > 0 ? t : undefined;
}

/** Parse a dollars string to integer cents. Blank/NaN → null (so callers can omit or reject). */
function dollarsToCents(v: string): number | null {
  const t = v.trim();
  if (t === '') return null;
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

/** Cents → an editable dollars string, e.g. 150000 → "1500.00". */
function centsToDollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** The first 10 chars of an ISO date/datetime → a `<input type="date">` value. */
function dateInputValue(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

/** Read a File to base64 WITHOUT the `data:...;base64,` prefix (JSON-friendly upload body). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Could not read the file.'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'));
    reader.readAsDataURL(file);
  });
}

/* ------------------------------------------------------------------ *
 * Section 1 — edit the lease core.                                    *
 * ------------------------------------------------------------------ */

function LeaseEditForm({ lease, onClose }: { lease: LeaseDTO; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LeaseStatus>(lease.status);
  const [termType, setTermType] = useState<LeaseTermType>(lease.termType);
  const [startDate, setStartDate] = useState(dateInputValue(lease.startDate));
  const [endDate, setEndDate] = useState(dateInputValue(lease.endDate));
  const [rent, setRent] = useState(centsToDollars(lease.rentCents));
  const [deposit, setDeposit] = useState(lease.depositCents != null ? centsToDollars(lease.depositCents) : '');
  const [notes, setNotes] = useState(lease.notes ?? '');
  const [remindersEnabled, setRemindersEnabled] = useState(lease.remindersEnabled ?? false);

  const save = useMutation({
    mutationFn: () => {
      const rentCents = dollarsToCents(rent);
      if (rentCents == null || rentCents < 0) throw new Error('Enter a valid rent amount.');
      const body: LeaseUpdateInput = {
        status,
        termType,
        startDate,
        endDate: opt(endDate) ?? null,
        rentCents,
        depositCents: dollarsToCents(deposit),
        notes: opt(notes) ?? null,
        remindersEnabled,
      };
      return api.leases.update(lease.id, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'lease', lease.id] });
      onClose();
    },
  });

  return (
    <Dialog title="Edit lease" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
        <div>
          <label htmlFor="le-status">Status</label>
          <select id="le-status" value={status} onChange={(e) => setStatus(e.target.value as LeaseStatus)}>
            {LEASE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="le-reminders">
            <input
              id="le-reminders"
              type="checkbox"
              checked={remindersEnabled}
              onChange={(e) => setRemindersEnabled(e.target.checked)}
              style={{ marginRight: '0.5rem' }}
            />
            Send rent reminders to tenants on this lease
          </label>
        </div>
        <div>
          <label htmlFor="le-term">Term type</label>
          <select id="le-term" value={termType} onChange={(e) => setTermType(e.target.value as LeaseTermType)}>
            {TERM_TYPES.map((t) => (
              <option key={t} value={t}>
                {t === 'fixed' ? 'Fixed' : 'Month-to-month'}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="le-start">Start date</label>
          <input id="le-start" type="date" value={startDate} required onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div>
          <label htmlFor="le-end">End date (optional)</label>
          <input id="le-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <div>
          <label htmlFor="le-rent">Monthly rent (dollars)</label>
          <input
            id="le-rent"
            type="number"
            step="0.01"
            min="0"
            value={rent}
            required
            onChange={(e) => setRent(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="le-deposit">Deposit (dollars, optional)</label>
          <input
            id="le-deposit"
            type="number"
            step="0.01"
            min="0"
            value={deposit}
            onChange={(e) => setDeposit(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="le-notes">Notes (landlord-internal, optional)</label>
          <textarea id="le-notes" value={notes} rows={3} maxLength={2000} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="row">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Section 2 — add a tenant to the lease.                              *
 * ------------------------------------------------------------------ */

function AddTenantForm({ leaseId, onClose }: { leaseId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const tenantsQuery = useQuery({ queryKey: ['admin', 'tenants'], queryFn: () => api.tenants.list() });
  const [tenantId, setTenantId] = useState('');
  const [role, setRole] = useState<LeaseTenantRole>('co_tenant');
  const [isServiceMember, setIsServiceMember] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      if (tenantId === '') throw new Error('Choose a tenant to add.');
      const body: LeaseTenantAddInput = { tenantId, role, isServiceMember };
      return api.leases.addTenant(leaseId, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'lease', leaseId] });
      onClose();
    },
  });

  return (
    <Dialog title="Add tenant" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
        {tenantsQuery.isLoading ? (
          <Loading label="Loading tenants…" />
        ) : tenantsQuery.isError || !tenantsQuery.data ? (
          <StatusMessage tone="err">{errorMessage(tenantsQuery.error)}</StatusMessage>
        ) : (
          <div>
            <label htmlFor="at-tenant">Tenant</label>
            <select id="at-tenant" value={tenantId} required onChange={(e) => setTenantId(e.target.value)}>
              <option value="">— Select a tenant —</option>
              {tenantsQuery.data.tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.firstName} {t.lastName}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label htmlFor="at-role">Role</label>
          <select id="at-role" value={role} onChange={(e) => setRole(e.target.value as LeaseTenantRole)}>
            {TENANT_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div className="row">
          <input
            id="at-sm"
            type="checkbox"
            style={{ width: 'auto' }}
            checked={isServiceMember}
            onChange={(e) => setIsServiceMember(e.target.checked)}
          />
          <label htmlFor="at-sm" style={{ margin: 0 }}>
            Service member (applies the 1-month deposit cap)
          </label>
        </div>
        <div className="row">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Adding…' : 'Add tenant'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Section 3 — replace the payment schedule.                          *
 * ------------------------------------------------------------------ */

interface ScheduleRow {
  dayOfMonth: string;
  amount: string;
  label: string;
}

function ScheduleEditForm({ lease, onClose }: { lease: LeaseDTO; onClose: () => void }) {
  const queryClient = useQueryClient();
  const initial: ScheduleRow[] =
    lease.schedule && lease.schedule.installments.length > 0
      ? lease.schedule.installments.map((i) => ({
          dayOfMonth: String(i.dayOfMonth),
          amount: centsToDollars(i.amountCents),
          label: i.label ?? '',
        }))
      : [{ dayOfMonth: '1', amount: '', label: '' }];
  const [rows, setRows] = useState<ScheduleRow[]>(initial);

  function updateRow(idx: number, patch: Partial<ScheduleRow>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setRows((prev) => [...prev, { dayOfMonth: '1', amount: '', label: '' }]);
  }
  function removeRow(idx: number) {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  }

  const save = useMutation({
    mutationFn: () => {
      if (rows.length === 0) throw new Error('Add at least one installment.');
      const withAmount = rows.filter((r) => r.amount.trim() !== '');
      // The server requires amounts on ALL installments (explicit split, Σ==rent) or NONE (even
      // split). A partial set can never satisfy that, so reject it before the request.
      if (withAmount.length !== 0 && withAmount.length !== rows.length)
        throw new Error('Enter an amount on every installment, or leave them all blank for an even split.');
      const supplyAmounts = withAmount.length === rows.length;
      const installments: ScheduleReplaceInput['installments'] = rows.map((r) => {
        const day = Number.parseInt(r.dayOfMonth, 10);
        if (!Number.isInteger(day) || day < 1 || day > 31) throw new Error('Day of month must be between 1 and 31.');
        const label = opt(r.label) ?? null;
        if (supplyAmounts) {
          const cents = dollarsToCents(r.amount);
          if (cents == null || cents < 0) throw new Error('Enter a valid amount on every installment.');
          return { dayOfMonth: day, amountCents: cents, label };
        }
        return { dayOfMonth: day, label };
      });
      const body: ScheduleReplaceInput = { cadence: 'monthly', installments };
      return api.leases.replaceSchedule(lease.id, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'lease', lease.id] });
      onClose();
    },
  });

  return (
    <Dialog title="Edit payment schedule" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
        <p className="muted">
          Leave every amount blank for an even split of the {formatCents(lease.rentCents)} rent, or set an amount on
          every installment (they must sum to the rent).
        </p>
        {rows.map((r, idx) => (
          <fieldset key={idx} className="stack" style={{ border: '1px solid var(--border, #ccc)', padding: '0.5rem' }}>
            <legend>Installment {idx + 1}</legend>
            <div>
              <label htmlFor={`si-day-${idx}`}>Day of month</label>
              <input
                id={`si-day-${idx}`}
                type="number"
                min="1"
                max="31"
                value={r.dayOfMonth}
                required
                onChange={(e) => updateRow(idx, { dayOfMonth: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor={`si-amt-${idx}`}>Amount (dollars, optional)</label>
              <input
                id={`si-amt-${idx}`}
                type="number"
                step="0.01"
                min="0"
                value={r.amount}
                onChange={(e) => updateRow(idx, { amount: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor={`si-label-${idx}`}>Label (optional)</label>
              <input
                id={`si-label-${idx}`}
                value={r.label}
                maxLength={200}
                onChange={(e) => updateRow(idx, { label: e.target.value })}
              />
            </div>
            <div>
              <button
                type="button"
                className="secondary"
                disabled={rows.length === 1}
                onClick={() => removeRow(idx)}
              >
                Remove<span className="visually-hidden"> installment {idx + 1}</span>
              </button>
            </div>
          </fieldset>
        ))}
        <div className="row">
          <button type="button" className="secondary" onClick={addRow}>
            Add installment
          </button>
        </div>
        <div className="row">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save schedule'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Section 4 — post a ledger entry.                                    *
 * ------------------------------------------------------------------ */

function PostEntryForm({ leaseId, onClose }: { leaseId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [type, setType] = useState<LedgerEntryTypeInput>('charge');
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [method, setMethod] = useState<LedgerMethodInput>('check');
  const [reference, setReference] = useState('');
  const [fee, setFee] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const isPayment = type === 'payment';

  const save = useMutation({
    mutationFn: () => {
      const amountCents = dollarsToCents(amount);
      if (amountCents == null || amountCents <= 0) throw new Error('Enter an amount greater than zero.');
      const body: PostEntryInput = {
        type,
        amountCents,
        memo: opt(memo) ?? null,
        method: isPayment ? method : null,
        reference: opt(reference) ?? null,
        feeCents: isPayment ? 0 : (dollarsToCents(fee) ?? 0),
        effectiveDate: opt(effectiveDate) ?? null,
      };
      return api.ledger.post(leaseId, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'ledger', leaseId] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'lease', leaseId] });
      onClose();
    },
  });

  return (
    <Dialog title="Post ledger entry" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
        <div>
          <label htmlFor="pe-type">Type</label>
          <select id="pe-type" value={type} onChange={(e) => setType(e.target.value as LedgerEntryTypeInput)}>
            {ENTRY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="pe-amount">Amount (dollars)</label>
          <input
            id="pe-amount"
            type="number"
            step="0.01"
            min="0"
            value={amount}
            required
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        {isPayment && (
          <div>
            <label htmlFor="pe-method">Method</label>
            <select id="pe-method" value={method} onChange={(e) => setMethod(e.target.value as LedgerMethodInput)}>
              {LEDGER_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label htmlFor="pe-reference">Reference (required for check / money order / cashier's check)</label>
          <input
            id="pe-reference"
            value={reference}
            maxLength={200}
            onChange={(e) => setReference(e.target.value)}
          />
        </div>
        {!isPayment && (
          <div>
            <label htmlFor="pe-fee">Fee (dollars, optional)</label>
            <input
              id="pe-fee"
              type="number"
              step="0.01"
              min="0"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
            />
          </div>
        )}
        <div>
          <label htmlFor="pe-memo">Memo (optional)</label>
          <input id="pe-memo" value={memo} maxLength={500} onChange={(e) => setMemo(e.target.value)} />
        </div>
        <div>
          <label htmlFor="pe-effective">Effective date (optional)</label>
          <input id="pe-effective" type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
        </div>
        <div className="row">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Posting…' : 'Post entry'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Section 5 — upload the executed-lease PDF.                          *
 * ------------------------------------------------------------------ */

function ExecutedPdfUpload({ leaseId }: { leaseId: string }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a PDF to upload.');
      const contentBase64 = await fileToBase64(file);
      return api.leases.uploadDocument(leaseId, {
        filename: file.name || 'lease.pdf',
        contentType: 'application/pdf',
        contentBase64,
        title: opt(title) ?? null,
      });
    },
    onSuccess: () => {
      setFile(null);
      setTitle('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'lease', leaseId] });
    },
  });

  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        upload.mutate();
      }}
    >
      {upload.isError && <StatusMessage tone="err">{errorMessage(upload.error)}</StatusMessage>}
      <div>
        <label htmlFor="ex-file">Executed lease (PDF)</label>
        <input
          id="ex-file"
          type="file"
          accept="application/pdf"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </div>
      <div>
        <label htmlFor="ex-title">Title (optional)</label>
        <input id="ex-title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div>
        <button type="submit" disabled={upload.isPending || !file}>
          {upload.isPending ? 'Uploading…' : 'Upload executed lease'}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ *
 * The page.                                                           *
 * ------------------------------------------------------------------ */

type OpenDialog = 'edit' | 'addTenant' | 'schedule' | 'post' | null;

export function AdminLeaseDetail() {
  const { leaseId = '' } = useParams();
  const queryClient = useQueryClient();
  const leaseQuery = useQuery({
    queryKey: ['admin', 'lease', leaseId],
    queryFn: () => api.leases.get(leaseId),
    enabled: leaseId !== '',
  });
  const ledgerQuery = useQuery({
    queryKey: ['admin', 'ledger', leaseId],
    queryFn: () => api.ledger.list(leaseId),
    enabled: leaseId !== '',
  });
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const { principal } = useAuth();
  const isOwner = principal?.role === 'owner';

  const removeTenant = useMutation({
    mutationFn: (tenantId: string) => api.leases.removeTenant(leaseId, tenantId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'lease', leaseId] }),
  });

  const receipt = useMutation({
    mutationFn: (entryId: string) => api.ledger.receipt(leaseId, entryId),
    onSuccess: (resp) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'ledger', leaseId] });
      window.open(resp.url, '_blank', 'noopener');
    },
  });

  if (leaseId === '') return <ErrorState message="Missing lease id." />;
  if (leaseQuery.isLoading) return <Loading label="Loading lease…" />;
  if (leaseQuery.isError || !leaseQuery.data)
    return <ErrorState message={errorMessage(leaseQuery.error)} onRetry={() => void leaseQuery.refetch()} />;

  const lease = leaseQuery.data;
  const warning = lease.depositValidation?.warning ?? null;

  return (
    <>
      <section aria-labelledby="lease-heading" className="stack">
        <p>
          <Link to="/admin/leases">← Leases</Link>
        </p>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h1 id="lease-heading">Lease</h1>
          <button type="button" onClick={() => setDialog('edit')}>
            Edit lease
          </button>
        </div>
        {warning && <StatusMessage tone="warn">{warning}</StatusMessage>}
        <dl className="card kv-grid">
          <div>
            <dt className="muted">Unit</dt>
            <dd>{lease.unitLabel ?? lease.unitId}</dd>
          </div>
          <div>
            <dt className="muted">Status</dt>
            <dd>
              <span className="badge">{lease.status}</span>
            </dd>
          </div>
          <div>
            <dt className="muted">Term</dt>
            <dd>{lease.termType === 'fixed' ? 'Fixed' : 'Month-to-month'}</dd>
          </div>
          <div>
            <dt className="muted">Rent</dt>
            <dd>{formatCents(lease.rentCents)}</dd>
          </div>
          <div>
            <dt className="muted">Deposit</dt>
            <dd>{lease.depositCents != null ? formatCents(lease.depositCents) : '—'}</dd>
          </div>
          <div>
            <dt className="muted">Start</dt>
            <dd>{formatDate(lease.startDate)}</dd>
          </div>
          <div>
            <dt className="muted">End</dt>
            <dd>{lease.endDate ? formatDate(lease.endDate) : '—'}</dd>
          </div>
        </dl>
        {lease.notes && <p className="muted">{lease.notes}</p>}
      </section>

      <section aria-labelledby="tenants-heading" className="stack">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2 id="tenants-heading">Tenants</h2>
          <button type="button" onClick={() => setDialog('addTenant')}>
            Add tenant
          </button>
        </div>
        {removeTenant.isError && <StatusMessage tone="err">{errorMessage(removeTenant.error)}</StatusMessage>}
        {lease.tenants.length === 0 ? (
          <p className="muted">No tenants on this lease yet.</p>
        ) : (
          <div className="table-wrap card" style={{ padding: 0 }}>
            <table>
              <caption className="visually-hidden">Tenants on this lease</caption>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Role</th>
                  <th scope="col">Service member</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {lease.tenants.map((t) => {
                  const name = t.tenant ? `${t.tenant.firstName} ${t.tenant.lastName}` : t.tenantId;
                  return (
                    <tr key={t.id}>
                      <td>{name}</td>
                      <td>{t.role}</td>
                      <td>{t.isServiceMember ? 'Yes' : 'No'}</td>
                      <td>
                        <button
                          type="button"
                          className="secondary"
                          disabled={removeTenant.isPending}
                          onClick={() => {
                            if (window.confirm(`Remove ${name} from this lease?`)) removeTenant.mutate(t.tenantId);
                          }}
                        >
                          Remove<span className="visually-hidden"> {name}</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-labelledby="schedule-heading" className="stack">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2 id="schedule-heading">Payment schedule</h2>
          <button type="button" onClick={() => setDialog('schedule')}>
            Edit schedule
          </button>
        </div>
        {!lease.schedule || lease.schedule.installments.length === 0 ? (
          <p className="muted">No schedule set — rent defaults to a single installment on the 1st.</p>
        ) : (
          <div className="table-wrap card" style={{ padding: 0 }}>
            <table>
              <caption className="visually-hidden">Payment schedule installments</caption>
              <thead>
                <tr>
                  <th scope="col">Day of month</th>
                  <th scope="col" className="num">
                    Amount
                  </th>
                  <th scope="col">Label</th>
                </tr>
              </thead>
              <tbody>
                {lease.schedule.installments.map((i) => (
                  <tr key={i.id}>
                    <td>{i.dayOfMonth}</td>
                    <td className="num">{formatCents(i.amountCents)}</td>
                    <td>{i.label || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-labelledby="ledger-heading" className="stack">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2 id="ledger-heading">Ledger</h2>
          <button type="button" onClick={() => setDialog('post')}>
            Post entry
          </button>
        </div>
        {receipt.isError && <StatusMessage tone="err">{errorMessage(receipt.error)}</StatusMessage>}
        {ledgerQuery.isLoading ? (
          <Loading label="Loading ledger…" />
        ) : ledgerQuery.isError || !ledgerQuery.data ? (
          <ErrorState message={errorMessage(ledgerQuery.error)} onRetry={() => void ledgerQuery.refetch()} />
        ) : (
          <>
            {(() => {
              const b = describeBalance(ledgerQuery.data.balanceCents);
              return (
                <p className="card" style={{ fontSize: '1.25rem' }}>
                  <strong>{b.amount}</strong> <span className="muted">{b.label}</span>
                </p>
              );
            })()}
            {ledgerQuery.data.entries.length === 0 ? (
              <p className="muted">No ledger entries yet.</p>
            ) : (
              <div className="table-wrap card" style={{ padding: 0 }}>
                <table>
                  <caption className="visually-hidden">Ledger entries</caption>
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Type</th>
                      <th scope="col" className="num">
                        Amount
                      </th>
                      <th scope="col">Method</th>
                      <th scope="col">Reference</th>
                      <th scope="col">Memo</th>
                      <th scope="col" className="num">
                        Balance
                      </th>
                      <th scope="col">Date</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerQuery.data.entries.map((e) => (
                      <tr key={e.id}>
                        <td>{e.seq}</td>
                        <td>{e.type}</td>
                        <td className="num">{formatCents(e.amountCents)}</td>
                        <td>{e.method || '—'}</td>
                        <td>{e.reference || '—'}</td>
                        <td>{e.memo || '—'}</td>
                        <td className="num">{formatCents(e.balanceAfterCents)}</td>
                        <td>{formatDate(e.effectiveDate ?? e.createdAt)}</td>
                        <td>
                          <button
                            type="button"
                            className="secondary"
                            disabled={receipt.isPending}
                            onClick={() => receipt.mutate(e.id)}
                          >
                            Receipt<span className="visually-hidden"> for entry {e.seq}</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      <section aria-labelledby="payact-heading" className="stack">
        <h2 id="payact-heading">Payment activity</h2>
        <p className="muted" style={{ margin: 0 }}>
          Every online bank payment step on this lease — opened, waiting on the tenant, processing (money in flight, not
          yet paid), cleared, failed or canceled. Only a cleared payment is on the ledger above.
        </p>
        <LeasePaymentActivity leaseId={lease.id} isOwner={isOwner} />
      </section>

      <section aria-labelledby="moveout-heading" className="stack">
        <h2 id="moveout-heading">Move-out</h2>
        <p className="card">
          Build the itemized security-deposit statement (Civil Code § 1950.5) — possession date and 21-day clock,
          deductions with receipts, move-in/move-out photos, and the final PDF.{' '}
          <Link to={`/admin/leases/${encodeURIComponent(lease.id)}/move-out`}>Open move-out report</Link>
        </p>
      </section>

      <section aria-labelledby="doc-heading" className="stack">
        <h2 id="doc-heading">Executed lease PDF</h2>
        {lease.executedDocumentId && (
          <StatusMessage tone="ok">
            An executed lease document is on file. Uploading a new PDF replaces it.
          </StatusMessage>
        )}
        <ExecutedPdfUpload leaseId={lease.id} />
      </section>

      {dialog === 'edit' && <LeaseEditForm lease={lease} onClose={() => setDialog(null)} />}
      {dialog === 'addTenant' && <AddTenantForm leaseId={lease.id} onClose={() => setDialog(null)} />}
      {dialog === 'schedule' && <ScheduleEditForm lease={lease} onClose={() => setDialog(null)} />}
      {dialog === 'post' && <PostEntryForm leaseId={lease.id} onClose={() => setDialog(null)} />}
    </>
  );
}
